import { QueryClient, QueryClientConfig } from '@tanstack/react-query';
import axios from 'axios';

/**
 * Centralized TanStack Query defaults.
 *
 * Rationale:
 *
 * - **staleTime: 30s** — TanStack's default is `0`, which means every mount
 *   triggers a refetch. For a B2B operations dashboard the data isn't
 *   second-fresh; treating it as fresh for 30 seconds eliminates a class of
 *   redundant network calls (e.g. navigating between sibling routes) while
 *   still feeling live. Per-query overrides (e.g. the infinite-scroll hooks'
 *   `5 * 60 * 1000`) continue to take precedence.
 *
 * - **gcTime: 10 minutes** — keep unused query data resident long enough that
 *   common back-navigation feels instant. The default of 5 minutes evicts
 *   too aggressively for a typical "drill in, drill back out" admin flow.
 *
 * - **retry: smart predicate** — TanStack's default is `3` retries on every
 *   failure. With the axios interceptor already handling 401 → refresh-token
 *   → replay, compounding 3 retries on top causes thundering-herd behavior
 *   against the auth endpoint. New policy:
 *     - 4xx client errors → no retry (auth/validation issues won't fix
 *       themselves on a retry; let the interceptor or the user resolve them)
 *     - 5xx / network errors → 1 retry (transient blips deserve one shot)
 *
 * - **refetchOnWindowFocus: true** *(kept as default)* — operators alt-tab
 *   between this app and other tools constantly; auto-syncing on focus is
 *   desirable. The 30s staleTime keeps this from being chatty.
 *
 * - **mutations.retry: 0** *(kept as default)* — never silently re-fire a
 *   mutation. A POST that "fails" may have actually succeeded; retrying it
 *   risks duplicate writes.
 *
 * Override per-query at the call site when you know better than the default.
 */
export const defaultQueryClientConfig: QueryClientConfig = {
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: (failureCount, error) => {
        if (axios.isAxiosError(error)) {
          const status = error.response?.status;
          if (status !== undefined && status >= 400 && status < 500) {
            return false;
          }
        }
        return failureCount < 1;
      },
    },
    mutations: {
      retry: 0,
    },
  },
};

/**
 * Returns a new `QueryClient` preconfigured with the shared app defaults.
 *
 * Use it once per app:
 *   - CRA-style apps (admin, partner, brandOwner): call at module level.
 *   - Next.js apps (tracker): call inside `useState(() => ...)` so a new
 *     client is created per session and stable across rerenders.
 *
 * Pass `overrides` to merge app-specific tweaks on top of the defaults.
 * Overrides are shallow-merged at the top level — pass a full
 * `defaultOptions` object if you need to customize it.
 */
export function createAppQueryClient(
  overrides?: QueryClientConfig
): QueryClient {
  return new QueryClient({
    ...defaultQueryClientConfig,
    ...overrides,
  });
}
